import {
	removeToken,
	removeLocalUserInfo
} from '@/assets/libs/storage.js'

/**
 * Vue3 版本网络请求封装（基于 fetch）
 * - 支持 get / post / put / del 四种方法
 * - 自动注入 token（localStorage）
 * - 统一业务 code 处理（200 成功 / 401 未登录 / 500 服务器错误）
 * - 支持请求重试
 */

// 请求基础配置
const TIMEOUT = 1000000; // 请求超时时间，单位毫秒
const BASE_URL = process.env.VUE_APP_BASE_API || ''; // 基础地址，可通过环境变量配置

// 默认请求头
const HEADER = {
	'Content-Type': 'application/json',
};

// 清除本地用户信息（token + 用户信息）
const clearLocalUser = () => {
	removeToken();
	removeLocalUserInfo();
};

// 构造请求头（注入 token）
const buildHeaders = (customHeaders = {}) => {
	const headers = {
		...HEADER,
		...customHeaders
	};
	const token = localStorage.getItem('userToken');
	if (token) {
		// 兼容 localStorage 自动添加的引号
		headers.Authorization = `Bearer ${token.replace(/^"|"$/g, '')}`;
	}
	return headers;
};

// 带超时的 fetch
const fetchWithTimeout = (url, options = {}, timeout = TIMEOUT) => {
	return Promise.race([
		fetch(url, options),
		new Promise((_, reject) =>
			setTimeout(() => reject(new Error('请求超时')), timeout)
		),
	]);
};

// 响应处理（业务 code 拦截）
// 注：当前后端约定 401/500 也以 HTTP 200 返回，业务 code 区分
const handleResponse = async (res) => {
	if (!res.ok) {
		// HTTP 状态码非 2xx
		if (res.status === 401) {
			console.error('登录身份过期，请重新登录!');
			clearLocalUser();
		}
		const errData = await res.json().catch(() => ({}));
		const msg = errData.message || `请求失败 (${res.status})`;
		console.error(msg);
		throw new Error(msg);
	}

	const data = await res.json();
	const code = data.code;

	// 业务 code：200 成功；401 未登录/登录过期；500 服务器错误
	if (code === 200) {
		return data;
	}
	if (code === 401) {
		console.error('登录身份过期，请重新登录!');
		clearLocalUser();
		throw new Error('登录身份过期');
	}
	if (code === 500) {
		console.error(data.msg || '服务器错误');
		throw new Error(data.msg || '服务器错误');
	}
	return data;
};

// 封装网络请求
const request = (options) => {
	const {
		url,
		method = 'GET',
		data = {},
		headers = {}
	} = options;
	const fullUrl = BASE_URL + url;
	const upperMethod = method.toUpperCase();

	const fetchOptions = {
		method: upperMethod,
		headers: buildHeaders(headers),
	};

	// GET/DELETE：参数拼接 URL；POST/PUT：放 body
	let requestUrl = fullUrl;
	if (['GET', 'DELETE'].includes(upperMethod)) {
		const params = new URLSearchParams(data).toString();
		if (params) {
			requestUrl = `${fullUrl}${fullUrl.includes('?') ? '&' : '?'}${params}`;
		}
	} else {
		fetchOptions.body = JSON.stringify(data);
	}

	return fetchWithTimeout(requestUrl, fetchOptions)
		.then(handleResponse)
		.catch((err) => {
			if (err.message === 'Failed to fetch') {
				console.error('网络请求失败');
			}
			return Promise.reject(err);
		});
};

// 请求重试机制
const retryRequest = (options, retries = 3) => {
	return new Promise((resolve, reject) => {
		const attempt = (attemptsLeft) => {
			request(options)
				.then(resolve)
				.catch((err) => {
					if (attemptsLeft > 0) {
						console.log(`请求失败，重试中... 剩余重试次数: ${attemptsLeft}`);
						attempt(attemptsLeft - 1);
					} else {
						reject(err);
					}
				});
		};
		attempt(retries);
	});
};

// HTTP 方法封装
const get = (url, data = {}, headers = {}) => {
	return request({
		url,
		method: 'GET',
		data,
		headers,
	});
};

const post = (url, data = {}, headers = {}) => {
	return request({
		url,
		method: 'POST',
		data,
		headers,
	});
};

const put = (url, data = {}, headers = {}) => {
	return request({
		url,
		method: 'PUT',
		data,
		headers,
	});
};

const del = (url, data = {}, headers = {}) => {
	return request({
		url,
		method: 'DELETE',
		data,
		headers,
	});
};

// 导出封装的请求方法
export default {
	get,
	post,
	put,
	del,
	retryRequest,
};
